// src/views/words-lists/slice/actions.ts
// ==========================================
// POBIERANIE LIST
// ==========================================

import { createAsyncThunk } from '@reduxjs/toolkit';
import ApiService from '../../../services/ApiService';

export const fetchWordsListPage = createAsyncThunk(
  'wordsList/fetchPage',
  async (page: number = 1) => {
    const response = await ApiService.wordsList.list(page);

    return response.data;
  },
);

export const fetchWordsList = createAsyncThunk(
  'wordsList/fetchOne',
  async (id: string) => {
    const response = await ApiService.wordsList.getOne(id);

    return response.data;
  },
);

export const fetchRootWordsLists = createAsyncThunk(
  'wordsList/fetchRootLists',
  async () => {
    const response = await ApiService.wordsList.getRootLists();

    return response.data;
  },
);

export const fetchWordsListsByFolder = createAsyncThunk(
  'wordsList/fetchByFolder',
  async (folderId: number) => {
    const response = await ApiService.wordsList.getByFolder(folderId);

    return response.data;
  },
);

// ==========================================
// TWORZENIE I EDYCJA
// ==========================================

export const createWordsList = createAsyncThunk(
  'wordsList/create',
  async (data: {
    name: string;
    description?: string;
    folderId?: number;
  }) => {
    const response = await ApiService.wordsList.create(data);

    return response.data;
  },
);

// ==========================================
// DUPLIKOWANIE I USUWANIE
// ==========================================

// Serwer tworzy kopię w folderze oryginału, stąd idzie ona do wybranego (`null` to katalog główny).
// Nieudane przeniesienie nie cofa kopii — `isInTargetFolder: false` znaczy, że została obok oryginału
export const duplicateWordsList = createAsyncThunk(
  'wordsList/duplicate',
  async ({ id, sourceFolderId, targetFolderId }: {
    id: number;
    sourceFolderId: number | null;
    targetFolderId: number | null;
  }) => {
    const { data: copy } = await ApiService.wordsList.duplicate(id);

    if (targetFolderId === sourceFolderId) {
      return { copy, isInTargetFolder: true };
    }

    try {
      await ApiService.folders.move({
        itemId: copy.id,
        itemType: 'list',
        targetFolderId,
        position: 0,
      });
    } catch (e) {
      return { copy, isInTargetFolder: false };
    }

    return { copy, isInTargetFolder: true };
  },
);

export const deleteWordsList = createAsyncThunk(
  'wordsList/delete',
  async (id: number) => {
    await ApiService.wordsList.delete(id);

    return id;
  },
);

// ==========================================
// PRZENOSZENIE MIĘDZY FOLDERAMI
// ==========================================

export const moveWordsListToFolder = createAsyncThunk(
  'wordsList/moveToFolder',
  async ({ listId, folderId }: { listId: number; folderId: number | null }) => {
    const response = await ApiService.wordsList.moveToFolder(listId, folderId);

    return response.data;
  },
);

// ==========================================
// AKCJE POMOCNICZE
// ==========================================

// ✅ POPRAWKA: Usuń typowanie RootState, użyj any
export const refreshWordsListPage = createAsyncThunk(
  'wordsList/refreshPage',
  async (_, { getState }) => {
    const state = getState() as any; // ✅ Zmienione z RootState na any
    const currentPage = state.wordsList?.currentPage || 1;
    const response = await ApiService.wordsList.list(currentPage);

    return response.data;
  },
);

export const resetWordsListData = createAsyncThunk(
  'wordsList/reset',
  async () => null,
);

// ==========================================
// SETTERY DLA BŁĘDÓW I STANÓW LOADING
// ==========================================

export const setWordsListError = (error: string | null) => ({
  type: 'wordsList/setError',
  payload: error,
});

export const setWordsListLoading = (isLoading: boolean) => ({
  type: 'wordsList/setLoading',
  payload: isLoading,
});

export const setDeletingWordsListError = (error: string | null) => ({
  type: 'wordsList/setDeletingError',
  payload: error,
});

export const setDuplicatingWordsListError = (error: string | null) => ({
  type: 'wordsList/setDuplicatingError',
  payload: error,
});

// ==========================================
// AKCJE DLA LOKALNEGO STATE
// ==========================================

export const setCurrentWordsList = (wordsList: any) => ({
  type: 'wordsList/setCurrent',
  payload: wordsList,
});

export const updateWordsInCurrentList = (words: any[]) => ({
  type: 'wordsList/updateWords',
  payload: words,
});

// ==========================================
// BATCH OPERATIONS
// ==========================================

export const bulkDeleteWordsLists = createAsyncThunk(
  'wordsList/bulkDelete',
  async (ids: number[]) => {
    const promises = ids.map((id) => ApiService.wordsList.delete(id));
    await Promise.all(promises);

    return ids;
  },
);

// ==========================================
// HELPER DO TWORZENIA LISTY W FOLDERZE
// ==========================================

export const createWordsListInFolder = createAsyncThunk(
  'wordsList/createInFolder',
  async (data: {
    name: string;
    description?: string;
    folderId?: number;
  }) => {
    const response = await ApiService.wordsList.create(data);

    return response.data;
  },
);

// ==========================================
// EXPORT TYPE dla TypeScript
// ==========================================

export type WordsListActions =
  | ReturnType<typeof fetchWordsListPage.pending>
  | ReturnType<typeof fetchWordsListPage.fulfilled>
  | ReturnType<typeof fetchWordsListPage.rejected>
  | ReturnType<typeof fetchWordsList.pending>
  | ReturnType<typeof fetchWordsList.fulfilled>
  | ReturnType<typeof fetchWordsList.rejected>
  | ReturnType<typeof createWordsList.pending>
  | ReturnType<typeof createWordsList.fulfilled>
  | ReturnType<typeof createWordsList.rejected>
  | ReturnType<typeof deleteWordsList.pending>
  | ReturnType<typeof deleteWordsList.fulfilled>
  | ReturnType<typeof deleteWordsList.rejected>
  | ReturnType<typeof duplicateWordsList.pending>
  | ReturnType<typeof duplicateWordsList.fulfilled>
  | ReturnType<typeof duplicateWordsList.rejected>
  | ReturnType<typeof moveWordsListToFolder.pending>
  | ReturnType<typeof moveWordsListToFolder.fulfilled>
  | ReturnType<typeof moveWordsListToFolder.rejected>
  | ReturnType<typeof refreshWordsListPage.pending>
  | ReturnType<typeof refreshWordsListPage.fulfilled>
  | ReturnType<typeof refreshWordsListPage.rejected>
  | ReturnType<typeof bulkDeleteWordsLists.pending>
  | ReturnType<typeof bulkDeleteWordsLists.fulfilled>
  | ReturnType<typeof bulkDeleteWordsLists.rejected>
  | ReturnType<typeof setWordsListError>
  | ReturnType<typeof setWordsListLoading>
  | ReturnType<typeof setDeletingWordsListError>
  | ReturnType<typeof setDuplicatingWordsListError>
  | ReturnType<typeof setCurrentWordsList>
  | ReturnType<typeof updateWordsInCurrentList>;
